import { getBackendUrl } from "../lib/utils";
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { User } from 'lucide-react';
import axios from 'axios';
import { getImageUrl } from '../lib/utils';

const Avatar = ({ src, alt, size = 'md', accent = 'blue' }) => {
  const sizeClasses = {
    sm: 'w-24 h-24 border-2',
    md: 'w-28 h-28 border-[3px]',
    lg: 'w-36 h-36 border-4',
    xl: 'w-40 h-40 border-4',
  };
  const accentClasses = {
    blue: 'border-blue-100',
    amber: 'border-amber-100',
    emerald: 'border-emerald-100',
    slate: 'border-slate-200',
  };

  return (
    <div className={`rounded-full overflow-hidden mb-4 bg-slate-100 flex items-center justify-center ${sizeClasses[size]} ${accentClasses[accent]}`}>
      {src ? (
        <img src={getImageUrl(src)} alt={alt} className="w-full h-full object-cover object-center" />
      ) : (
        <User className="text-slate-300" size={size === 'xl' ? 64 : size === 'lg' ? 56 : 48} strokeWidth={1.5} />
      )}
    </div>
  );
};

const PersonCard = ({ person, variant = 'default' }) => {
  const variants = {
    coordinator: {
      wrap: 'w-80 border-amber-200 ring-4 ring-amber-50/70 shadow-xl shadow-amber-100/40',
      avatarSize: 'xl',
      accent: 'amber',
      role: 'text-amber-600',
      name: 'text-2xl',
      badge: 'bg-amber-50 text-amber-700 border-amber-200',
    },
    president: {
      wrap: 'w-72 border-blue-200 ring-4 ring-blue-50/60 shadow-xl shadow-blue-100/40',
      avatarSize: 'lg',
      accent: 'blue',
      role: 'text-blue-600',
      name: 'text-xl',
      badge: 'bg-blue-50 text-blue-700 border-blue-200',
    },
    vice: {
      wrap: 'w-64 border-blue-100 shadow-lg',
      avatarSize: 'md',
      accent: 'blue',
      role: 'text-blue-600',
      name: 'text-lg',
      badge: 'bg-blue-50 text-blue-700 border-blue-100',
    },
    member: {
      wrap: 'w-60 border-slate-200 shadow-md',
      avatarSize: 'md',
      accent: 'slate',
      role: 'text-slate-600',
      name: 'text-base',
      badge: 'bg-slate-100 text-slate-600 border-slate-200',
    },
    audit: {
      wrap: 'w-60 border-emerald-100 shadow-md',
      avatarSize: 'md',
      accent: 'emerald',
      role: 'text-emerald-700',
      name: 'text-base',
      badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    },
  };
  const v = variants[variant] || variants.member;
  return (
    <div className={`bg-white rounded-2xl border overflow-hidden flex flex-col items-center p-6 text-center relative transition-all duration-300 hover:-translate-y-1 ${v.wrap}`}>
      <Avatar src={person.image} alt={person.name} size={v.avatarSize} accent={v.accent} />
      <h3 className={`font-bold text-slate-900 ${v.name}`}>{person.name}</h3>
      <span className={`mt-3 inline-block text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full border ${v.badge}`}>
        {person.role}
      </span>
    </div>
  );
};

const BoardOfDirectors = () => {
  const [data, setData] = useState({
    generalCoordinator: {
      role: "Genel Koordinatör",
      name: "Nihat TUNALI",
      image: "/images/board/nihat-tunali.jpg",
    },
    president: {
      role: "Yönetim Kurulu Başkanı",
      name: "Fikret ŞİMŞEK",
      image: "/images/board/fikret-simsek.jpg",
    },
    viceChairman: {
      role: "Yönetim Kurulu Başkan Yardımcısı",
      name: "İsmail KOCAAHMET",
      image: null,
    },
    boardMembers: [
      {
        role: "Yönetim Kurulu Üyesi",
        name: "Hacı Murat DUMAN",
        image: "/images/board/haci-murat-duman.jpg",
      },
      {
        role: "Yönetim Kurulu Üyesi",
        name: "Köksal ASLANTAŞ",
        image: "/images/board/koksal-aslantas.jpg",
      },
      {
        role: "Yönetim Kurulu Üyesi",
        name: "Mahmut Mert AKTAŞ",
        image: "/images/board/mahmut-mert-aktas.jpg",
      },
    ],
    auditCommittee: [
      {
        role: "Denetim Kurulu Üyesi",
        name: "Nihat TUNALI",
        image: "/images/board/nihat-tunali.jpg",
      },
      {
        role: "Denetim Kurulu Üyesi",
        name: "Recep ÖZEN",
        image: null,
      },
      {
        role: "Denetim Kurulu Üyesi",
        name: "Hakan MEMİŞ",
        image: null,
      },
    ]
  });

  useEffect(() => {
    const backendUrl = getBackendUrl();
    axios.get(`${backendUrl}/api/settings`)
      .then(res => {
        if (res.data.board_members) {
          setData(res.data.board_members);
        }
      })
      .catch(console.error);
  }, []);

  return (
    <div className="pt-32 pb-24 min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
      <div className="container mx-auto px-6 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h4 className="text-blue-600 font-semibold uppercase tracking-[0.2em] text-xs mb-3">Kurumsal</h4>
          <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-6 tracking-tight">Yönetim Kadromuz</h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Kooperatifimizi geleceğe taşıyan, alanında uzman ve vizyoner yönetim kadromuz.
          </p>
        </motion.div>

        {/* GENEL KOORDİNATÖR */}
        {data.generalCoordinator && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="flex flex-col items-center"
          >
            <PersonCard person={data.generalCoordinator} variant="coordinator" />
            <div className="w-px h-12 bg-slate-200"></div>
          </motion.div>
        )}

        {/* YÖNETİM KURULU */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex flex-col items-center"
        >
          <div className="inline-flex items-center gap-3 mb-10">
            <div className="h-px w-12 bg-slate-300"></div>
            <h2 className="text-xs uppercase tracking-[0.3em] font-bold text-slate-500">Yönetim Kurulu</h2>
            <div className="h-px w-12 bg-slate-300"></div>
          </div>

          {/* Başkan */}
          {data.president && <PersonCard person={data.president} variant="president" />}
          <div className="w-px h-12 bg-blue-200"></div>

          {/* Başkan Yardımcısı */}
          {data.viceChairman && <PersonCard person={data.viceChairman} variant="vice" />}
          <div className="w-px h-12 bg-blue-200"></div>

          {/* Üyeler */}
          <div className="relative w-full max-w-5xl">
            <div className="absolute top-0 left-[16%] right-[16%] h-px bg-blue-200 hidden md:block"></div>
            <div className="flex flex-wrap justify-center gap-8 md:gap-6 pt-8">
              {data.boardMembers && data.boardMembers.map((member, index) => (
                <div key={index} className="flex flex-col items-center relative">
                  <div className="absolute -top-8 w-px h-8 bg-blue-200 hidden md:block"></div>
                  <PersonCard person={member} variant="member" />
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* DENETİM KURULU */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-24 pt-16 border-t border-slate-200"
        >
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-3 mb-3">
              <div className="h-px w-12 bg-emerald-300"></div>
              <h2 className="text-xs uppercase tracking-[0.3em] font-bold text-emerald-700">Denetim Kurulu</h2>
              <div className="h-px w-12 bg-emerald-300"></div>
            </div>
            <p className="text-slate-500 max-w-xl mx-auto">
              Kooperatif faaliyetlerinin şeffaflığını ve mevzuata uygunluğunu denetleyen kurul üyelerimiz.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-8">
            {data.auditCommittee && data.auditCommittee.map((member, index) => (
              <PersonCard key={index} person={member} variant="audit" />
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default BoardOfDirectors;
